import React from 'react';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import isAdmin from '../../../../helpers/isAdmin';
import isUpperManagement from '../../../../helpers/isUpperManagement';
import isHRUser from '../../../../helpers/isHRUser';
import { isDirector } from '../../../../helpers';
import isAuthorized from '../../../../helpers/isAuthorized';

type DirectoryActionHandlers = {
  resendPaperworkAlert: (e: any, row: any) => void;
  resendCredentialsAlert: (e: any, id: string) => void;
  handleRemoveAccess: (e: any, row: any) => void;
  handleTerminateUserOpen: (row: any) => void;
  handlePutOnLeave: (e: any, row: any) => void;
  handleUpdatePayment: (e: any, row: any) => void;
  handleEndPutOnLeave: (e: any, row: any) => void;
  handleRevokeTerminationOpen: (row: any) => void;
  handleRevokeUserOpen: (row: any) => void;
  handleResendEmail: (e: any, row: any) => void;
  handleRehireOpen: (row: any) => void;
  confirmChangestatus: (e: any, id: string) => void;
  triggerAnnualReviewAlert: (e: any, row: any) => void;
};

interface DirectoryActionsDropdownProps {
  row: any;
  user: any;
  isMobile: boolean;
  handlers: DirectoryActionHandlers;
}

const DirectoryActionsDropdown = ({ row, user, isMobile, handlers }: DirectoryActionsDropdownProps) => {
  const {
    resendPaperworkAlert,
    resendCredentialsAlert,
    handleRemoveAccess,
    handleTerminateUserOpen,
    handlePutOnLeave,
    handleUpdatePayment,
    handleEndPutOnLeave,
    handleRevokeTerminationOpen,
    handleRevokeUserOpen,
    handleResendEmail,
    handleRehireOpen,
    confirmChangestatus,
    triggerAnnualReviewAlert,
  } = handlers;

  const canManageActive = isAdmin(user) || isUpperManagement(user) || isHRUser(user) || isDirector(user);

  return (
    <UncontrolledDropdown nav>
      <DropdownToggle className={`pr-0 ${isMobile ? 'pt-1' : ''}`} nav style={{ paddingLeft: '0' }}>
        <Button style={{ border: 'none', boxShadow: 'none', background: 'none' }}>
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </Button>
      </DropdownToggle>
      {row.status === 'ACTIVE' && <i className="bg-success mr-2" style={{ verticalAlign: 'baseline' }} />}
      {row.status === 'TERMINATED' && <i className="bg-danger mr-2" style={{ verticalAlign: 'baseline' }} />}
      {row.status === 'HIRED' && <i className="bg-yellow mr-2" style={{ verticalAlign: 'baseline' }} />}
      {row.status === 'OFFER_SENT' && <i className="bg-teal mr-2" style={{ verticalAlign: 'baseline' }} />}
      <DropdownMenu className="dropdown-menu-arrow" right container="body" positionFixed style={{ zIndex: 9999 }}>
        {row.status === 'ACTIVE' && !row?.terminationDate && (
          <>
            <DropdownItem onClick={(e: any) => resendPaperworkAlert(e, row)}>
              <span>Resend Paperwork</span>
            </DropdownItem>
            {canManageActive && (
              <DropdownItem onClick={(e: any) => triggerAnnualReviewAlert(e, row)}>
                <span>Send Annual Review Now</span>
              </DropdownItem>
            )}
            {canManageActive && (
              <>
                <DropdownItem onClick={(e: any) => resendCredentialsAlert(e, row.id)}>
                  <span>Resend Credentials</span>
                </DropdownItem>
                <DropdownItem onClick={(e) => handleRemoveAccess(e, row)}>
                  <span>Remove Access</span>
                </DropdownItem>
                {isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Create' }, user) && (
                  <DropdownItem onClick={() => handleTerminateUserOpen(row)}>
                    <span>Terminate</span>
                  </DropdownItem>
                )}
                <DropdownItem onClick={(e) => handlePutOnLeave(e, row)}>
                  <span>Put On Leave</span>
                </DropdownItem>
                {user.id !== row.userId && (
                  <DropdownItem onClick={(e) => handleUpdatePayment(e, row)}>
                    <span>Update Pay Rate</span>
                  </DropdownItem>
                )}
              </>
            )}
          </>
        )}

        {row.status === 'LOA' && (
          <>
            {isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Create' }, user) && (
              <DropdownItem onClick={() => handleTerminateUserOpen(row)}>
                <span>Terminate</span>
              </DropdownItem>
            )}
            <DropdownItem onClick={(e) => handleEndPutOnLeave(e, row)}>
              <span>End LOA</span>
            </DropdownItem>
          </>
        )}

        {row.status === 'ACTIVE' && row?.terminationDate && (
          <>
            <DropdownItem onClick={(e) => handleRemoveAccess(e, row)}>
              <span>Remove Access</span>
            </DropdownItem>
            {isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Revoke' }, user) && (
              <DropdownItem onClick={() => handleRevokeTerminationOpen(row)}>
                <span>Revoke Termination</span>
              </DropdownItem>
            )}
          </>
        )}

        {(row.status === 'OFFER_SENT' || row.status === 'HIRED') && (
          <DropdownItem onClick={() => handleRevokeUserOpen(row)}>
            <span>Revoke</span>
          </DropdownItem>
        )}

        {(row.status === 'HIRED' || row.status === 'BLOCKED') && row?.id && (
          <>
            {row.status === 'HIRED' && (
              <DropdownItem onClick={(e: any) => resendPaperworkAlert(e, row)}>
                <span>Resend Paperwork</span>
              </DropdownItem>
            )}
            <DropdownItem onClick={(e: any) => confirmChangestatus(e, row.id)}>
              <span>Active</span>
            </DropdownItem>
          </>
        )}

        {row.status === 'OFFER_SENT' && (
          <DropdownItem onClick={(e: any) => handleResendEmail(e, row)}>
            <span>Resend Email</span>
          </DropdownItem>
        )}

        {(row.status === 'TERMINATED' ||
          row.status === 'DENIED' ||
          row.status === 'DECLINED' ||
          row.status === 'REVOKED') && (
          <>
            {row.status === 'TERMINATED' &&
              isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Revoke' }, user) && (
                <DropdownItem onClick={() => handleRevokeTerminationOpen(row)}>
                  <span>Revoke Termination</span>
                </DropdownItem>
              )}
            <DropdownItem onClick={() => handleRehireOpen(row)}>
              <span>Rehire</span>
            </DropdownItem>
          </>
        )}

        {row.status === 'BLOCKED' && (
          <>
            {isAuthorized({ moduleName: 'EmployeeTerminate', functionName: 'Create' }, user) && (
              <DropdownItem onClick={() => handleTerminateUserOpen(row)}>
                <span>Terminate</span>
              </DropdownItem>
            )}
          </>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default DirectoryActionsDropdown;
